import { useToast } from 'context/providers/ToastProvider';
import useToken from 'hooks/useToken';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';

const PreferenciasComplementoPago = ({
    register,
    selectedValuesDDL,
    setSelectedValuesDDL,
    errors,
    clearError,
    setValue,
    idPago,
    vUUIDFactura,
    rfcSeleccionado
}) => {
    const { token, setToken } = useToken();
    const { showErrorToast } = useToast();

    const [formasPago, setFormasPago] = useState([]);
    const [monedas, setMonedas] = useState([]);

    // Guard to prevent errors if register is not a function
    const registerField = (name = '') => register && typeof register === 'function' ? register(name) : {};

    // Helper to format date to local YYYY-MM-DDTHH:MM
    const formatLocalDateTime = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    /**
     * Obtiene las Formas de Pago
     */
    const ObtenerFormasPago = async () => {
        const response = await fetch("/api/contabilidad/CatalogoSAT/ObtenerFormasDePago",
            {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            })
            .catch((error) => {
                showErrorToast('Hubo un problema con la peticion Fetch:' + error);
            });

        if (response.ok) {
            const data = await response.json();

            if (data.iResponseType == 1) {
                var ObjDetalles = JSON.parse(data.detalles);
                setFormasPago(ObjDetalles.table2);
            } else {
                showErrorToast(data.vMessage);
            }
        } else {
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                location.href = "";
            }
        }
    }

    /**
     * Obtiene las Monedas
     */
    const ObtenerCatMonedas = async () => {
        const response = await fetch("/api/contabilidad/CatalogoSAT/ObtenerCatMonedas",
            {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            })
            .catch((error) => {
                showErrorToast('Hubo un problema con la peticion Fetch:' + error);
            });

        if (response.ok) {
            const data = await response.json();

            if (data.iResponseType == 1) {
                var ObjDetalles = JSON.parse(data.detalles);
                setMonedas(ObjDetalles.table2);
            } else {
                showErrorToast(data.vMessage);
            }
        } else {
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                location.href = "";
            }
        }
    }


    /**
     * Obtiene la información del pago
     */
    const ObtenerInformacionDelPago = async () => {
        const response = await fetch("/api/contabilidad/facturas/FacturaOrden/ObtenerInformacionDelPago?idPago=" + idPago, {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json'
            }
        })
            .catch((error) => {
                showErrorToast('Hubo un problema con la peticion Fetch:' + error);
            });

        if (response.ok) {
            const data = await response.json();
            if (data.iResponseType == 1) {
                const pagoInfo = JSON.parse(data.detalles).table1[0];
                // Assuming pagoInfo has the payment data
                // Populate the fields
                const fechaPago = pagoInfo.dFechaPago ? formatLocalDateTime(new Date(pagoInfo.dFechaPago)) : '';
                setValue('dFechaPago', fechaPago);
                const fechaEmision = pagoInfo.dFechaEmision ? formatLocalDateTime(new Date(pagoInfo.dFechaEmision)) : '';
                setValue('dFechaEmision', fechaEmision);
                setValue('vMoneda', pagoInfo.vMoneda || '');
                setValue('iNumeroParcialidad', pagoInfo.iNumeroParcialidad || '');
                setValue('dSaldoAnterior', pagoInfo.dSaldoAnterior || '');
                setValue('dImportePago', pagoInfo.dImportePago || '');
                setValue('dSaldoInsoluto', pagoInfo.dSaldoInsoluto || '');
                setValue('vNumOperacion', pagoInfo.vReferencia || '');
                setValue('dMonto', pagoInfo.dImportePago || '');
                setValue('dTipoCambio', pagoInfo.dTipoCambio || '');
                setValue('vFormaPago', pagoInfo.vClave || '');
                setValue('vMetodoPagoDR', 'PPD');
                setValue('vObjetoImpDR', '02');
                setSelectedValuesDDL({
                    ...selectedValuesDDL,
                    vMoneda: pagoInfo.vMoneda || 'MXN',
                    vFormaPago: pagoInfo.vClave || '',
                    vMetodoPagoDR: 'PPD',
                    vObjetoImpDR: '02'
                });
            } else {
                showErrorToast(data.vMessage);
            }
        } else {
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                location.href = "";
            }
        }
    }

    /**
     * Llama al endpoint para obtener el siguiente folio de una serie
     * @param {string} serie
     * @param {string} vRFC
     * @returns {Promise<number|null>}
     */
    const fetchSiguienteFolio = async (serie, vRFC) => {
        if (!serie || !vRFC || vRFC.trim() === '') {
            console.warn('fetchSiguienteFolio: serie o vRFC no válidos', { serie, vRFC });
            return null;
        }

        try {
            const response = await fetch(`/api/contabilidad/facturas/FormularioFactura/ObtenerSiguienteFolioPago?serie=${encodeURIComponent(serie)}&vRFC=${encodeURIComponent(vRFC)}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (response.ok) {
                const data = await response.json();
                if (data && data.siguienteFolio) {
                    return data.siguienteFolio;
                }
            } else if (response.status === 401) {
                console.error('Unauthorized: Token inválido o expirado');
                setToken(null);
                location.href = "";
            }
        } catch (e) {
            console.error('Error al obtener siguiente folio:', e);
        }
        return null;
    };

    useEffect(() => {
        ObtenerFormasPago();
        ObtenerCatMonedas();
        setValue('vIdDocumento', vUUIDFactura || '');
        setSelectedValuesDDL(prev => ({
            ...prev,
            vMetodoPagoDR: 'PPD',
            vObjetoImpDR: '02',
            vSerie: 'P'
        }));

        if (idPago) {
            ObtenerInformacionDelPago();
        }
    }, [idPago]);

    // Effect to fetch folio when RFC is selected or serie changes
    useEffect(() => {
        const serie = selectedValuesDDL.vSerie || 'P';

        if (rfcSeleccionado && rfcSeleccionado.trim() !== '') {
            console.log('Obteniendo folio para serie:', serie, 'RFC:', rfcSeleccionado);
            fetchSiguienteFolio(serie, rfcSeleccionado).then(folio => {
                if (folio) {
                    console.log('Folio obtenido:', folio);
                    setValue('vFolio', folio.toString());
                } else {
                    console.warn('No se pudo obtener el folio');
                }
            });
        } else {
            console.log('RFC no disponible aún');
        }
    }, [selectedValuesDDL.vSerie, rfcSeleccionado]);

    return (
        <>

            <h5 className="mb-3">Detalles del Pago</h5>
            <Row className="g-2">
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Monto</Form.Label>
                        <Form.Control
                            size="sm"
                            type="number"
                            step="0.01"
                            {...registerField('dMonto')}
                        />
                        {errors.dMonto && <div className="text-danger small">{errors.dMonto}</div>}
                    </Form.Group>
                </Col>

                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Moneda</Form.Label>
                        <Form.Select
                            size="sm"
                            value={selectedValuesDDL.vMoneda}
                            onChange={(e) => {
                                setSelectedValuesDDL({ ...selectedValuesDDL, vMoneda: e.target.value });
                                clearError('vMoneda');
                            }}
                        >
                            <option value="">Seleccione...</option>
                            {monedas.map((moneda, index) => (
                                <option key={index} value={moneda.vClaveMoneda}>
                                    {moneda.vClaveMoneda}
                                </option>
                            ))}
                        </Form.Select>
                        {errors.vMoneda && <div className="text-danger small">{errors.vMoneda}</div>}
                    </Form.Group>
                </Col>
                {selectedValuesDDL.vMoneda && selectedValuesDDL.vMoneda !== "MXN" && (
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Tipo de Cambio</Form.Label>
                            <Form.Control
                                size="sm"
                                type="number"
                                step="0.000001"
                                {...registerField('dTipoCambio')}
                                onChange={(e) => {
                                    setValue('dTipoCambio', e.target.value);
                                    clearError('dTipoCambio');
                                }}
                            />
                            {errors.dTipoCambio && <div className="text-danger small">{errors.dTipoCambio}</div>}
                        </Form.Group>
                    </Col>
                )}
            </Row>
            <Row className="g-2">
                <Col md={12}>
                    <Form.Group>
                        <Form.Label>Forma de Pago</Form.Label>
                        <Form.Select
                            size="sm"
                            value={selectedValuesDDL.vFormaPago}
                            onChange={(e) => {
                                setSelectedValuesDDL({ ...selectedValuesDDL, vFormaPago: e.target.value });
                                clearError('vFormaPago');
                            }}
                        >
                            <option value="">Seleccione...</option>
                            {formasPago.map((forma, index) => (
                                <option key={index} value={forma.vClave}>
                                    {forma.vClave} - {forma.vDescripcionFormaPago}
                                </option>
                            ))}
                        </Form.Select>
                        {errors.vFormaPago && <div className="text-danger small">{errors.vFormaPago}</div>}
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <Form.Group>
                        <Form.Label>Número Operación</Form.Label>
                        <Form.Control
                            size="sm"
                            type="text"
                            {...registerField('vNumOperacion')}
                        />
                        {errors.vNumOperacion && <div className="text-danger small">{errors.vNumOperacion}</div>}
                    </Form.Group>
                </Col>
            </Row>
            <hr />

        </>
    );
};

PreferenciasComplementoPago.propTypes = {
    register: PropTypes.func.isRequired,
    selectedValuesDDL: PropTypes.object.isRequired,
    setSelectedValuesDDL: PropTypes.func.isRequired,
    errors: PropTypes.object.isRequired,
    clearError: PropTypes.func.isRequired,
    setValue: PropTypes.func.isRequired,
    idPago: PropTypes.number,
    vUUIDFactura: PropTypes.string,
    rfcSeleccionado: PropTypes.string
};

export default PreferenciasComplementoPago;
